import {
  TableInstance,
  useColumnOrder,
  useExpanded,
  useFlexLayout,
  useGlobalFilter,
  useGroupBy,
  usePagination,
  useResizeColumns,
  useSortBy,
  useTable,
} from "react-table";
import { useExportData } from "react-table-plugins";
import { TableScrollContainer } from "./TableScrollContainer";
import { TableBody } from "./TableBody";
import { TableHeader } from "./TableHeader";
import { TableWrapper } from "./TableWrapper";
import { TableProps, UserColumnsConfig, TableCellIds } from "./types/index";
import { Center } from "../Center";
import { getExportFileName } from "./utils/exportFileName";
import { mergeColumnOrder } from "./utils/mergeColumnOrder";
import { useSelector } from "react-redux";
import { RootState } from "store";
import { TableFilter } from "./TableFilter";
import { TablePagination } from "./TablePagination";
import { TableToolbar } from "./TableToolbar.tsx";
import { CSSProperties, useEffect, useMemo } from "react";
import PulseLoader from "react-spinners/PulseLoader";
import { toast } from "react-hot-toast";

export const defaultColumnValues = {
  minWidth: 200,
  width: 150,
};

// Resolve the id react-table will assign to a raw column definition. Columns
// with a function accessor (e.g. "Carrier", "Service") have no `id` and no
// string accessor, so react-table falls back to the string Header. Deriving the
// id as `col.id || col.accessor` here produced the accessor *function* for those
// columns, so they were filtered out of any saved order and never reordered.
const resolveColumnId = (col: any): string | undefined =>
  col.id ||
  (typeof col.accessor === "string" ? col.accessor : undefined) ||
  (typeof col.Header === "string" ? col.Header : undefined);

export const Table = <T extends object>({
  data,
  columns,
  onSearchSubmit,
  searchInitialValue,
  tableKey,
  onRefresh,
  filters,
  horizontalFilters = false,
  allowExport = false,
  manualPagination = true,
  isLoading,
  recordCount,
  pageCount = 25,
  pageSize = 10,
  pageIndex: pageIndexProp = 0,
  handleColumnSort,
  sort,
  onRowClick,
  onNextPage,
  onPreviousPage,
  erroredColumns = [],
  downloadOptions = [],
  TableButtons,
  FilterComponent,
  defaultColumn,
  tableRowQuantityChange,
  userColumns,
  hiddenColumns = [],
  hideSearch,
  hideDownload,
  emptyState,
  showScanAndPrint,
  footerLeftContent,
  stickyHeader = true,
}: TableProps<T>) => {
  const extendedUseTable = useTable as (...args: any[]) => TableInstance<T>;
  const { isFilterSidebarOpen, theme } = useSelector((state: RootState) => {
    return state.tableSlice;
  });

  const defaultColumnSize = defaultColumn || defaultColumnValues;

  // Build column order: checkbox first (if exists), then saved order
  const buildColumnOrder = () => {
    const savedOrder = (userColumns as UserColumnsConfig)?.columnOrder || [];
    const checkboxColumn = columns.find(col => col.id === TableCellIds.CHECKBOX);
    
    // Filter saved order to only include columns that actually exist in current columns array
    // Resolve ids the same way react-table does, so function-accessor columns
    // (e.g. Carrier, Service) are matched by their Header-derived id.
    const existingColumnIds = columns.map(resolveColumnId);

    // Columns the saved order predates (e.g. one added since the user last
    // dragged) go next to their definition neighbour, not to the end.
    const validSavedOrder = mergeColumnOrder(
      savedOrder.filter(id => id !== TableCellIds.CHECKBOX),
      existingColumnIds.filter(id => id && id !== TableCellIds.CHECKBOX)
    );
    
    // If checkbox exists, put it first
    if (checkboxColumn) {
      return [TableCellIds.CHECKBOX, ...validSavedOrder];
    }
    
    return validSavedOrder;
  };

  const useTableData = extendedUseTable(
    {
      columns,
      data,
      defaultColumn: defaultColumnSize,
      manualPagination,
      pageCount:
        manualPagination === true
          ? pageCount
          : Math.round(data.length / pageSize),
      initialState: {
        pageIndex: pageIndexProp,
        pageSize,
        columnOrder: buildColumnOrder(),
        hiddenColumns: userColumns
          ? Object.keys(userColumns).filter(key => key !== 'columnOrder' && !(userColumns as UserColumnsConfig)[key])
          : hiddenColumns,
      },
      // Without this, any columns/data identity change (every parent render)
      // resets visibility to mount-time state, undoing dropdown toggles.
      // The effect below keeps visibility in sync with userColumns instead.
      autoResetHiddenColumns: false,
      paginateExpandedRows: false,
      // Lazy-loaded: exportData pulls in jspdf/xlsx/papaparse. Returning false
      // tells react-table-plugins the download is handled by the module itself.
      getExportFileBlob: (args: any) => {
        import("./utils/exportData")
          .then((mod) => mod.getExportFileBlob(args))
          .catch(() => {
            toast.error("Export failed to load. Please try again.");
          });
        return false;
      },
      getExportFileName: getExportFileName("filename"),
    },
    useGlobalFilter,
    useColumnOrder,
    useGroupBy,
    useFlexLayout,
    useSortBy,
    useExpanded,
    usePagination,
    useResizeColumns,
    useExportData
  );

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    page,
    visibleColumns,
    previousPage,
    nextPage,
    prepareRow,
    state: { pageIndex },
    exportData,
    setColumnOrder,
    setHiddenColumns,
  } = useTableData;

  // Apply visibility whenever the saved config changes (toggle persisted,
  // status-tab switch). Keyed on content, not object identity — the parent
  // rebuilds userColumns every render.
  const hiddenColumnsKey = useMemo(() => {
    if (!userColumns) return null;
    return Object.keys(userColumns)
      .filter(key => key !== 'columnOrder' && !(userColumns as UserColumnsConfig)[key])
      .sort()
      .join(',');
  }, [userColumns]);

  useEffect(() => {
    if (hiddenColumnsKey === null || !setHiddenColumns) return;
    setHiddenColumns(hiddenColumnsKey === '' ? [] : hiddenColumnsKey.split(','));
  }, [hiddenColumnsKey, setHiddenColumns]);

  // Update column order when userColumns changes (e.g., after page refresh with saved order)
  const memoizedColumnOrder = useMemo(() => {
    if (!userColumns) return [];
  
    const checkboxColumn = columns.find(col => col.id === TableCellIds.CHECKBOX);
  
    const savedOrder = (userColumns as UserColumnsConfig)?.columnOrder || [];
    const existingColumnIds = columns.map(resolveColumnId);
    const validSavedOrder = mergeColumnOrder(
      savedOrder.filter(id => id !== TableCellIds.CHECKBOX),
      existingColumnIds.filter(id => id && id !== TableCellIds.CHECKBOX)
    );
  
    return checkboxColumn ? [TableCellIds.CHECKBOX, ...validSavedOrder] : validSavedOrder;
  }, [userColumns, columns]);
  
  useEffect(() => {
    if (setColumnOrder && memoizedColumnOrder.length > 0) {
      const currentOrder = useTableData.state.columnOrder;
      const isDifferent =
        memoizedColumnOrder.length !== currentOrder.length ||
        memoizedColumnOrder.some((colId, idx) => colId !== currentOrder[idx]);
  
      if (isDifferent) {
        setColumnOrder(memoizedColumnOrder);
      }
    }
  }, [memoizedColumnOrder, setColumnOrder, useTableData.state.columnOrder]);

  const handleNextPage = () => {
    if (onNextPage) {
      onNextPage();
    } else {
      nextPage();
    }
  };

  const handlePreviousPage = () => {
    if (onPreviousPage) {
      onPreviousPage();
    } else {
      previousPage();
    }
  };

  const showToolbar =
    allowExport ||
    filters ||
    Boolean(onSearchSubmit && !hideSearch) ||
    Boolean(onRefresh);

  const override: CSSProperties = {
    display: "block",
    margin: "0 auto",
    borderColor: "red",
    zIndex: 1,
  };

  return (
    <div className="flex flex-col flex-1 w-full overflow-hidden">
      {showToolbar && (
        <TableToolbar
          filters={filters}
          horizontalFilters={horizontalFilters}
          onSearchSubmit={onSearchSubmit}
          searchInitialValue={searchInitialValue}
          tableKey={tableKey}
          onRefresh={onRefresh}
          useTableData={useTableData}
          allowExport={allowExport}
          downloadOptions={downloadOptions}
          exportFunction={() => exportData("csv", false)}
          TableButtons={TableButtons}
          userColumns={userColumns}
          hideSearch={hideSearch}
          hideDownload={hideDownload}
          showScanAndPrint={showScanAndPrint}
          recordCount={recordCount}
        />
      )}

      <div className="flex flex-1 overflow-hidden max-md:flex-col">
        {filters && isFilterSidebarOpen && !horizontalFilters && (
          <TableFilter filters={filters} FilterComponent={FilterComponent} />
        )}

        <TableWrapper>
          {isLoading && (
            <div className="absolute w-full h-full">
              <Center>
                <PulseLoader
                  color={theme === "dark" ? "#ffffff" : "#000000"}
                  loading={isLoading}
                  cssOverride={override}
                  size={20}
                  aria-label="Loading Spinner"
                  data-testid="loader"
                />
              </Center>
            </div>
          )}
          <TableScrollContainer
            emptyContent={
              !isLoading && data.length === 0 ? (
                <>
                  No results
                  {emptyState}
                </>
              ) : undefined
            }
          >
            {/* w-full stretches narrow tables to the container; react-table's
                inline min-width (totalColumnsWidth) still wins for wide tables,
                which then h-scroll. The min-w-full class alone loses to that
                inline style, so it can't do the stretching. */}
            <div
              {...getTableProps()}
              className="flex flex-col w-full min-w-full self-start Table"
            >
              <TableHeader
                headerGroups={headerGroups}
                sort={sort}
                handleColumnSort={handleColumnSort}
                erroredColumns={erroredColumns}
                stickyHeader={stickyHeader}
              />

              {!isLoading && data.length === 0 ? null : (
                <TableBody
                  page={page}
                  isLoading={false}
                  visibleColumns={visibleColumns}
                  getTableBodyProps={getTableBodyProps}
                  onRowClick={(row) => {
                    onRowClick ? onRowClick(row) : null;
                  }}
                  prepareRow={prepareRow}
                  erroredColumns={erroredColumns}
                />
              )}
            </div>
          </TableScrollContainer>
          <div className="px-4">
            <TablePagination<T>
              page={page}
              previousPage={handlePreviousPage}
              nextPage={handleNextPage}
              pageIndex={pageIndex}
              pageCount={pageCount}
              pageSize={pageSize}
              total={manualPagination === true ? recordCount : data.length}
              tableRowQuantityChange={tableRowQuantityChange}
              footerLeftContent={footerLeftContent}
            />
          </div>
        </TableWrapper>
      </div>
    </div>
  );
};
